import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

const reviews = [
  {
    name: "Ryan Griffiths",
    company: "Turfit",
    companyUrl: "https://turfit.com/",
    reviewUrl: "https://maps.app.goo.gl/eBuwgJmY6jQ8tFr8A",
    date: "June 2026",
    text: "Working with Arno has been amazing. They are a top notch and professional team. He met with me promtly to go over his strategy and how it would apply to our business and we quickly scheduled time to get started on work. They have been punctual and already produced fantastic content for us. Looking forward to an ongoing advertising relationship.",
  },
  {
    name: "Hassan Erakat, MP",
    company: "Progress Construction & Renovation",
    companyUrl: "https://www.progresscillc.com/",
    reviewUrl: "https://maps.app.goo.gl/jFgbPGNX7CFrZZpA6",
    date: "April 2026",
    text: "Working with a marketing agency can sometimes feel like a chore, especially in the construction world. This team was a breath of fresh air. They recently came out to one of our completed remodeling projects to capture footage for future ads, and the entire experience was seamless. From the moment they arrived, it was clear they were pros — full setup with professional lighting, multiple angles, and a clear vision. They had scripts ready and knew exactly how to direct the flow of the day. They were also incredibly respectful of the homeowner's space. If you're looking for an agency that actually knows how to produce high-quality content without the headache, I can't recommend them enough.",
  },
  {
    name: "Dave Ellis",
    company: "The Racing Line",
    companyUrl: "https://theracingline.app/",
    reviewUrl: "https://maps.app.goo.gl/p7Ah7UYWBjmPep1R7",
    date: "December 2025",
    text: "Really really helpful people. They run my Meta ad campaigns, and have suggested changes to the pricing and onboarding of my product, both of which improved conversion rates. They're generally just great at marketing; including the product side of things. Fully recommend.",
  },
  {
    name: "Chelsy Davis",
    company: "Salon Owner",
    companyUrl: "",
    reviewUrl: "",
    date: "",
    text: "We had the pleasure of working with some incredibly talented content creators who visited our salon, and we couldn't be happier with the experience. They were professional, creative, and captured the atmosphere of our business beautifully. The video they created showcased our services in a way that felt authentic, engaging, and visually stunning. Their attention to detail, positive energy, and ability to highlight what makes our salon special exceeded our expectations. The final content was high-quality and has already helped us increase engagement and showcase our work to both current and future clients. We highly recommend them to any business looking to elevate its brand through professional content creation. Thank you for helping bring our vision to life — we appreciate you, Arnold!",
  },
  {
    name: "PC Ryan",
    company: "",
    companyUrl: "",
    reviewUrl: "",
    date: "",
    text: "We had a great experience working with this agency. Following the onboarding interview, they produced draft scripts and screenplays tailored to our business. Filming took only an hour or two, and the entire process was easy thanks to their professional guidance. I had already seen the client videos they published and knew that was the kind of message I wanted our business to communicate. I'm very happy with the brand storytelling and the personal, human service. Thank you!",
  },
];

export const Reviews = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const goTo = (i: number) => {
    const el = scroller.current;
    if (!el) return;
    const total = reviews.length;
    const next = ((i % total) + total) % total;
    const card = el.querySelectorAll('[data-card]')[next] as HTMLElement | undefined;
    if (!card) return;
    el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: 'smooth' });
    setActive(next);
  };

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onScroll = () => {
      const cards = Array.from(el.querySelectorAll('[data-card]')) as HTMLElement[];
      const center = el.scrollLeft + el.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const d = Math.abs(c.offsetLeft - el.offsetLeft + c.offsetWidth / 2 - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      setActive(best);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="section section-dark" ref={ref}>
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            What Our Partners Say
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Verified Google reviews from business owners we work with
          </p>
          <Link to="/blog/kaia-bowls-viral-restaurant-video" className="inline-block mt-3 text-primary font-semibold hover:underline">
            Latest case study: Kaia Bowls' 6M-view restaurant video →
          </Link>
        </motion.div>

        <div
          ref={scroller}
          className="max-w-7xl mx-auto flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar items-start pb-2 -mx-4 px-4"
        >
          {reviews.map((review, i) => {
            const isLong = review.text.length > 320;
            const isOpen = !!expanded[review.name];
            return (
            <motion.div
              key={review.name}
              data-card
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 * i }}
              className="glass-card shrink-0 snap-center w-[85vw] sm:w-[380px] md:w-[420px]"
            >
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, j) => (
                  <span key={j} className="text-yellow-400 text-lg">★</span>
                ))}
              </div>
              <blockquote className="text-base leading-relaxed text-foreground/90 italic mb-2">
                "{isLong && !isOpen ? `${review.text.slice(0, 320).trimEnd()}…` : review.text}"
              </blockquote>
              {isLong && (
                <button
                  type="button"
                  onClick={() => setExpanded((s) => ({ ...s, [review.name]: !isOpen }))}
                  className="text-sm font-semibold text-primary hover:underline mb-4"
                >
                  {isOpen ? 'Read less' : 'Read more'}
                </button>
              )}
              <div className="mt-2">
                <p className="font-semibold text-foreground">{review.name}</p>
                {review.company && (
                <p className="text-sm text-muted-foreground">
                  {review.companyUrl ? (
                    <a href={review.companyUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {review.company}
                    </a>
                  ) : (
                    review.company
                  )}
                </p>
                )}
                {review.reviewUrl && (
                <p className="text-xs text-muted-foreground mt-1">
                  <a href={review.reviewUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    {review.date} — Google Review ↗
                  </a>
                </p>
                )}
              </div>
            </motion.div>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            type="button"
            onClick={() => goTo(active - 1)}
            aria-label="Previous review"
            className="w-11 h-11 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary/10 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z" /></svg>
          </button>
          <button
            type="button"
            onClick={() => goTo(active + 1)}
            aria-label="Next review"
            className="w-11 h-11 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary/10 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M8.6 16.6 10 18l6-6-6-6-1.4 1.4 4.6 4.6z" /></svg>
          </button>
        </div>
        <div className="flex items-center justify-center gap-2 mt-4">
          {reviews.map((r, i) => (
            <button
              key={r.name}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Go to review ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === active ? 'w-6 bg-primary' : 'w-2 bg-primary/25 hover:bg-primary/50'}`}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <a href="/#contact" className="btn-hero-primary">
            Join Our Partners
          </a>
        </motion.div>
      </div>
    </section>
  );
};
